'use client';
import { useDebounceFn, useSize } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReset } from '@/app/components/icons/BaseLineReset';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/app/components/ui/dialog';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useDragAndDrop, useGlobalModal, useIsScreen } from '@/app/hooks';
import type {
  ChangedImagePrompt,
  FormImagePromptWithSize,
  ImagePrompt,
  ImageRange,
  ImageResolution,
  ImageUploadFile,
  ImageUploadFileInfo,
} from '@/app/types';
import { cn } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { CropOptionDisplay } from './components/CropOptionDisplay';
import { ImageTooltipContent } from './components/ImageTooltipContent';
import { LoadingOverlay } from './components/LoadingOverlay';
import { ThumbContent } from './components/ThumbContent';
import useClipMask from './use-clip-mask';
import useCropOptions from './use-crop-options';
import { calculateRangeAndScale, getNewRange, TIME_OUT } from './utils';

type Img2VideoImgCropModalProps = {
  onClose: () => void; //关闭弹窗
  onCancel: () => void; //点击取消按钮
  onConfirm: (changedImagePrompts: ChangedImagePrompt[]) => Promise<void>;
  cropImagePrompts: (FormImagePromptWithSize | null)[] | null;
  activeIndex: number; // 默认激活的图片索引
  maxImageSize: number;
  scene?: UploadScene; // 上传场景
  maxImageCount?: number; // 最大图片数量
  handleClickImage: (idx: number) => void;
  handleExchange: (fromIndex: number, toIndex: number) => void;
  onChange: (index: number, file: ImageUploadFile, fileUploadInfo?: ImageUploadFileInfo) => void;
  onUploadImage?: (file: ImageUploadFile, fileUploadInfo?: ImageUploadFileInfo) => void; // 新增图片
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
};

export const Img2VideoImgCropModal: FC<Img2VideoImgCropModalProps> = ({
  onClose,
  onCancel,
  onConfirm,
  cropImagePrompts,
  activeIndex,
  maxImageSize,
  scene = UPLOAD_SCENE.VIDU,
  maxImageCount = 10,
  handleClickImage,
  handleExchange,
  onChange,
  onUploadImage,
  allowTempMode = false,
}) => {
  const [range, setRange] = useState<ImageRange>([0, 0, 0, 0]); // 裁剪区域（虚线框）
  const [saving, setSaving] = useState(false); // 是否正在保存
  const [imageLoaded, setImageLoaded] = useState(false); // 图片是否已加载完成
  const [selectedAspect, setSelectedAspect] = useState<'free' | number>('free'); //当前选中比例
  const [changedImagePrompts, setChangedImagePrompts] = useState<ChangedImagePrompt[]>([]); //缓存已修改的图片
  const [uploadingIndexes, setUploadingIndexes] = useState<Set<number>>(new Set()); // 正在上传的图片索引
  const maxImageRange = useRef<ImageRange>([0, 0, 0, 0]); // 裁剪最大区域（不能超过图片范围）
  const resizeScale = useRef<number>(1); // 图片的缩放比例

  const activeFormImagePrompt = useMemo(() => {
    return cropImagePrompts?.[activeIndex] || null;
  }, [activeIndex, cropImagePrompts]);
  const { id: activeImageId, prompt: activeImage } = activeFormImagePrompt ?? {};
  const activeImageSource = activeImage?.sourceImagePreview || null;
  const { isSmScreen } = useIsScreen();
  const t = useTranslations();
  const { freeAspectOption, defaultCropOptions } = useCropOptions();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const imgRef = useRef<HTMLImageElement>(null); // 图片的DOM节点
  const imgSize = useSize(imgRef); // 获取图片实际渲染尺寸

  // 图片 DOM 实际渲染尺寸（从 imgSize 派生）
  const domResolution: ImageResolution = imgSize ?? { width: 0, height: 0 };

  const aspectRatios = [freeAspectOption, ...defaultCropOptions]; //固定的裁剪选项

  // 初始化缓存
  useEffect(() => {
    if (cropImagePrompts && domResolution.width && domResolution.height && imageLoaded) {
      setChangedImagePrompts((prev) => {
        const updatedCache = [...prev];

        cropImagePrompts.forEach((formImagePrompt) => {
          if (!formImagePrompt?.prompt) return;

          const existingIndex = updatedCache.findIndex((cache) => cache.id === formImagePrompt.id);
          if (existingIndex === -1) {
            const imagePrompt = formImagePrompt.prompt;
            const { sourceImageWidth, sourceImageHeight, selectedImageRange } = imagePrompt;
            // 计算图片在容器中的显示范围
            const { scale, range: imageRange } = calculateRangeAndScale(
              { width: sourceImageWidth, height: sourceImageHeight },
              domResolution,
            );

            // 计算默认裁剪区域（基于图片原始的 selectedImageRange）
            const [x1, y1, x2, y2] = selectedImageRange ?? [0, 0, sourceImageWidth, sourceImageHeight];
            let defaultRange: ImageRange = [
              x1 * scale + imageRange[0],
              y1 * scale + imageRange[1],
              x2 * scale + imageRange[0],
              y2 * scale + imageRange[1],
            ];

            // 根据当前选中的比例调整裁剪区域
            if (selectedAspect !== 'free' && typeof selectedAspect === 'number') {
              // 固定比例：使用选中的比例值
              defaultRange = getNewRange(defaultRange, selectedAspect, imageRange);
            } else {
              // 自由比例：使用当前图片的实际比例（如果当前有图片的话）
              const [cx1, cy1, cx2, cy2] = range;
              const currentWidth = cx2 - cx1;
              const currentHeight = cy2 - cy1;
              const currentRatio = currentWidth / currentHeight;
              if (currentRatio > 0 && Number.isFinite(currentRatio)) {
                defaultRange = getNewRange(defaultRange, currentRatio, imageRange);
              }
            }

            const finalCache: ChangedImagePrompt = {
              id: formImagePrompt.id,
              range: defaultRange,
              cropRatio: selectedAspect,
              imagePrompt,
              containerSize: domResolution,
            };

            updatedCache.push(finalCache);
          }
        });
        return updatedCache;
      });
    }
  }, [cropImagePrompts, domResolution, imageLoaded]);

  useEffect(() => {
    setImageLoaded(false); //只要图片变化，就清空图片加载状态
  }, [activeImage]);

  useEffect(() => {
    if (cropImagePrompts === null) {
      setChangedImagePrompts([]); // 无图片时，清空缓存
      setSelectedAspect('free'); // 无图片时，重置为自由比例
    }
  }, [cropImagePrompts]);

  useEffect(() => {
    //小屏时重置为自由比例，且不展示缩略图和比例选项
    isSmScreen && handleAspectChange('free');
  }, [isSmScreen]);

  useEffect(() => {
    if (!imageLoaded || !imgSize || !activeImage) return;

    //图片加载成功后，更新裁剪区域
    const { width, height } = imgSize;
    const { sourceImageWidth, sourceImageHeight } = activeImage;
    if (!sourceImageWidth || !sourceImageHeight) return;
    const [x1, y1, x2, y2] = activeImage?.selectedImageRange ?? [0, 0, sourceImageWidth, sourceImageHeight];
    const { scale, range } = calculateRangeAndScale(
      {
        width: sourceImageWidth,
        height: sourceImageHeight,
      },
      {
        width,
        height,
      },
    );
    resizeScale.current = scale;
    maxImageRange.current = range;

    const targetChangedImagePrompt = changedImagePrompts.find((it) => it.id === activeImageId);
    if (targetChangedImagePrompt?.range && !targetChangedImagePrompt.range.every((it) => it === 0)) {
      setRange(targetChangedImagePrompt.range);
    } else {
      setRange([
        x1 * resizeScale.current + maxImageRange.current[0],
        y1 * resizeScale.current + maxImageRange.current[1],
        x2 * resizeScale.current + maxImageRange.current[0],
        y2 * resizeScale.current + maxImageRange.current[1],
      ]);
    }
  }, [imageLoaded, imgSize]);

  // 当前比例
  const currentAspectRatio = useMemo(() => {
    const [x1, y1, x2, y2] = range;
    const width = x2 - x1;
    const height = y2 - y1;
    return width / height;
  }, [range]);

  const { run: updateOtherImagesCaches } = useDebounceFn(
    () => {
      // 更新所有其他图片的缓存，使它们保持相同的 aspectRatio
      if (!cropImagePrompts) return;

      const updatedCaches: ChangedImagePrompt[] = [];

      cropImagePrompts.forEach((imagePrompt, idx) => {
        if (!imagePrompt?.prompt || idx === activeIndex) return; // 跳过当前图片

        const imageId = imagePrompt.id;
        const imageData = imagePrompt.prompt;
        const existingCache = changedImagePrompts.find((cache) => cache.id === imageId);

        if (existingCache) {
          const updatedCache: ChangedImagePrompt = {
            ...existingCache,
            cropRatio: selectedAspect,
          };

          // 计算缓存中 range 的当前比例
          const [x1, y1, x2, y2] = existingCache.range;
          const cacheWidth = x2 - x1;
          const cacheHeight = y2 - y1;
          const cacheAspectRatio = cacheWidth / cacheHeight;

          // 使用除法判断比例是否相等：商接近1且浮动不超过0.1表示比例相等
          const aspectRatioQuotient = cacheAspectRatio / currentAspectRatio;
          if (Math.abs(aspectRatioQuotient - 1) > 0.1) {
            const { range: imageRange } = calculateRangeAndScale(
              { width: imageData.sourceImageWidth, height: imageData.sourceImageHeight },
              existingCache.containerSize,
            );
            const newRange = getNewRange(existingCache.range, currentAspectRatio, imageRange);
            updatedCache.range = newRange;
          }
          updatedCaches.push(updatedCache);
        }
      });

      if (updatedCaches.length > 0) {
        setChangedImagePrompts((prev) => {
          const otherCaches = prev.filter((cache) => !updatedCaches.find((updated) => updated.id === cache.id));
          return [...otherCaches, ...updatedCaches];
        });
      }
    },
    {
      wait: TIME_OUT,
    },
  );

  //实际比例变化时会触发，更新所有其他图片的缓存
  useEffect(() => {
    updateOtherImagesCaches();
  }, [currentAspectRatio]);

  const { run: updateCurrentImageCache } = useDebounceFn(
    () => {
      if (activeImage && activeImageId) {
        const changedImagePrompt: ChangedImagePrompt = {
          id: activeImageId,
          range,
          cropRatio: selectedAspect,
          imagePrompt: activeImage,
          containerSize: domResolution,
        };
        setChangedImagePrompts((prev) => [...prev.filter((it) => it.id !== activeImageId), changedImagePrompt]);
      }
    },
    {
      wait: TIME_OUT,
    },
  );

  // 裁剪区域变化时，更新当前图片的缓存
  useEffect(() => {
    updateCurrentImageCache();
  }, [range]);

  // 剪裁蒙层
  const { clipMaskDom, handleMoving, handleMoveEnd } = useClipMask({
    range,
    selectedAspect,
    maxImageRange,
    imageScale: resizeScale.current,
    setRange,
  });

  const handleSaveCropImage = async () => {
    setSaving(true);

    // 构造包含当前图片的完整缓存数据
    let allChangedImagePrompts = [...changedImagePrompts];
    if (activeImageId && activeImage && activeFormImagePrompt) {
      const currentImagePrompt: ChangedImagePrompt = {
        id: activeImageId,
        range,
        cropRatio: selectedAspect,
        imagePrompt: activeImage,
        containerSize: domResolution,
      };
      allChangedImagePrompts = [...allChangedImagePrompts.filter((it) => it.id !== activeImageId), currentImagePrompt];
    }

    // 转换所有缓存数据的显示坐标为原图坐标
    const processedChangedImagePrompts = allChangedImagePrompts.map((changedPrompt) => {
      const { sourceImageWidth, sourceImageHeight } = changedPrompt.imagePrompt;

      // 使用该图片缓存时的容器尺寸进行计算
      const { scale, range: imageRange } = calculateRangeAndScale(
        { width: sourceImageWidth, height: sourceImageHeight },
        changedPrompt.containerSize,
      );

      // 将显示坐标转换为原图坐标
      const originalRange: ImageRange = [
        Math.floor((changedPrompt.range[0] - imageRange[0]) / scale),
        Math.floor((changedPrompt.range[1] - imageRange[1]) / scale),
        Math.floor((changedPrompt.range[2] - imageRange[0]) / scale),
        Math.floor((changedPrompt.range[3] - imageRange[1]) / scale),
      ];

      return {
        ...changedPrompt,
        range: originalRange,
      };
    });

    // 保存裁剪区域和选择的比例
    await onConfirm(processedChangedImagePrompts);
    setSaving(false);
    onClose();
  };

  // 更新比例
  const handleAspectChange = (targetCropRatio: 'free' | number) => {
    setSelectedAspect(targetCropRatio);
    if (targetCropRatio !== 'free') setRange(getNewRange(range, targetCropRatio, maxImageRange.current)); //根据新的比例，更新裁剪区域
    // 比例变化时候更新所有缓存中的cropRatio值
    setChangedImagePrompts((prev) =>
      prev.map((cache) => ({
        ...cache,
        cropRatio: targetCropRatio,
      })),
    );
  };

  const renderRemoveDom = (handleRemove: () => void, isLast: boolean) => {
    return (
      <div className="absolute top-[-8px] right-[-8px] z-10 hidden items-center justify-center transition-opacity group-hover:block sm:group-hover:hidden">
        <div
          className="flex size-5 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]"
          onClick={(e) => {
            e.stopPropagation();
            if (isLast) {
              openConfirmDialog({
                title: t('imgCropModal.deleteLast.title'),
                content: t('imgCropModal.deleteLast.content'),
                primaryButtonText: t('imgCropModal.deleteLast.confirm'),
                secondaryButtonText: t('imgCropModal.deleteLast.cancel'),
                onPrimaryAction: () => {
                  handleRemove();
                  onClose();
                },
              });
              return;
            }
            handleRemove();
          }}
        >
          <BaseLineClose className="text-base" />
        </div>
      </div>
    );
  };

  const handleReset = () => {
    handleAspectChange('free');
    if (maxImageRange.current) setRange([...maxImageRange.current]); //选择整个图片
  };

  const { onDragStart, onDragOver, onDropCapture } = useDragAndDrop({ onReorder: handleExchange });
  const renderImageDom = (id: string, prompt: ImagePrompt, removeButton: ReactNode, idx: number, isActive: boolean) => {
    // 获取当前图片的缓存数据
    const cachedData = changedImagePrompts.find((cache) => cache.id === id);

    // 获取当前图片应该使用的 range（仅当需要实时预览时使用）
    const imageRange: ImageRange | undefined = isActive ? range : cachedData?.range;

    // 非激活图片使用缓存的容器尺寸，避免切换时因 domResolution 重置为 0 导致闪烁
    const thumbDomResolution = isActive ? domResolution : cachedData?.containerSize;

    return (
      <CustomTooltip
        key={id}
        content={
          <ImageTooltipContent
            changedImagePrompts={changedImagePrompts}
            activeImageId={activeImageId}
            range={range}
            domResolution={domResolution}
            promptId={id}
            prompt={prompt}
          />
        }
        side="bottom"
        disableHoverableContent={true}
        trigger="hover"
      >
        <div
          onClick={() => handleClickImage(idx)}
          className={cn(
            'group relative h-16 w-16 cursor-pointer rounded-6 border-2 border-system-white',
            isActive && 'gradient-border-2 border-gradient-b-primary-dark',
          )}
          draggable={!!handleExchange}
          onDragStart={(e) => onDragStart(e, idx)}
          onDragOver={(e) => onDragOver(e)}
          onDropCapture={(e) => onDropCapture(e, idx)}
        >
          <ThumbContent isActive={isActive} prompt={prompt} range={imageRange} domResolution={thumbDomResolution} />
          {removeButton}
          {/* 拖动蒙层：防止拖动时变形 */}
          <div className="pointer-events-none absolute inset-0" />
        </div>
      </CustomTooltip>
    );
  };

  const renderImageCard = (idx: number, imagePrompt?: FormImagePromptWithSize | null) => {
    const isUploading = uploadingIndexes.has(idx); // 检查当前索引是否正在上传
    if (imagePrompt) {
      const total = cropImagePrompts?.filter((it) => it).length || 0;
      const { id, status, prompt } = imagePrompt;
      if ((status === 'done' || status === 'uploading') && prompt) {
        const isActive = activeIndex === idx;
        const handleRemoveImage = () => {
          if (prompt) {
            URL.revokeObjectURL(prompt.sourceImagePreview);
            if (prompt.selectedImagePreview) {
              URL.revokeObjectURL(prompt.selectedImagePreview);
            }
          }
          onChange(idx, {
            id,
            file: null,
            status: 'removed',
          });
          setChangedImagePrompts((prev) => prev.filter((it) => it.id !== id)); //将缓存中的数据删除
        };
        return renderImageDom(
          id,
          prompt,
          renderRemoveDom(() => handleRemoveImage(), total === 1),
          idx,
          isActive,
        );
      }
    } else {
      if (isUploading) return <LoadingOverlay key={idx} />; // 如果正在上传，显示 loading 状态
      return (
        <ImageUploader
          className="group flex h-16 w-16 flex-col items-center justify-center gap-2 rounded-8 border-2 border-system-white24 text-white hover:border-system-white36 hover:bg-system-white08"
          maxCount={1}
          scene={scene}
          getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
          onChange={(file, fileUploadInfo) => {
            onChange(idx, file, fileUploadInfo);
            if (file.status === 'uploading') {
              setUploadingIndexes((prev) => new Set(Array.from(prev).concat(idx)));
            } else {
              setUploadingIndexes((prev) => {
                const newSet = new Set(prev);
                newSet.delete(idx);
                return newSet;
              });
            }
          }}
          allowTempMode={allowTempMode}
        >
          <BaseLinePlus className="text-2xl opacity-80 group-hover:opacity-100" />
          <span className="text-sm opacity-80 group-hover:opacity-100">
            {t('create.char2VideoForm.element.addImages')}
          </span>
        </ImageUploader>
      );
    }
  };

  return (
    <Dialog open={cropImagePrompts !== null}>
      <DialogContent className="max-w-[836px] gap-1 md:h-4/5" variant="mobileFullScreen" closable={false}>
        <DialogTitle className="pl-4">{t('img2VideoImgCropModal.title')}</DialogTitle>
        <div className="flex-1 md:flex md:flex-col md:overflow-hidden">
          <div className="flex items-center justify-between pr-4 pl-4">
            <DialogDescription className="whitespace-pre-line">
              {t('img2VideoImgCropModal.descriptionNew')}
            </DialogDescription>

            {/* <ImageUploader
              key="replace-image"
              maxCount={1}
              className="mb-3"
              getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
              onChange={(file, fileUploadInfo) => onChange(activeIndex, file, fileUploadInfo)}
            >
              <Button
                variant="outline"
                className="h-10 border border-system-white24 bg-[#181C23] px-4 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
              >
                <BaseLineReplace className="mr-2 text-lg" />
                {t('imgCropModal.replace')}
              </Button>
            </ImageUploader> */}
          </div>
          <div
            onPointerMove={handleMoving}
            onPointerLeave={handleMoveEnd}
            onPointerUp={handleMoveEnd}
            className="relative h-[400px] w-full select-none p-4 md:flex-1 md:overflow-hidden"
          >
            {activeImageSource ? (
              <img
                src={activeImageSource}
                alt="image-source"
                id="crop-image-source"
                ref={imgRef}
                className="size-full object-contain"
                onLoad={() => setImageLoaded(true)}
              />
            ) : null}
            {clipMaskDom}
            {/* 图片加载中的 loading 状态 */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <Loading className="animate-spin text-4xl text-white" />
                </div>
              </div>
            )}
          </div>

          {/* 图片缩略图区域 */}
          {!isSmScreen && cropImagePrompts && cropImagePrompts.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              {cropImagePrompts.map((imagePrompt, idx) => {
                if (!imagePrompt) return null;
                return <div key={imagePrompt.id || idx}>{renderImageCard(idx, imagePrompt)}</div>;
              })}
              {/* 添加图片按钮 */}
              {onUploadImage && cropImagePrompts.length < maxImageCount && (
                <ImageUploader
                  className="group flex h-16 w-16 flex-col items-center justify-center gap-2 rounded-8 border-2 border-system-white24 text-white hover:border-system-white36 hover:bg-system-white08"
                  maxCount={maxImageCount - cropImagePrompts.length}
                  scene={scene}
                  getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                  onChange={(file, fileUploadInfo) => onUploadImage(file, fileUploadInfo)}
                  allowTempMode={allowTempMode}
                >
                  <BaseLinePlus className="text-2xl opacity-80 group-hover:opacity-100" />
                  <span className="text-sm opacity-80 group-hover:opacity-100">
                    {t('create.char2VideoForm.element.addImages')}
                  </span>
                </ImageUploader>
              )}
            </div>
          )}

          {/* 比例选择区域 */}
          {!isSmScreen && (
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {aspectRatios.map((item) => (
                <div
                  key={item.value}
                  className={cn(
                    'flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-system-white24 bg-[#0D0D1214] px-5 text-white',
                    selectedAspect === item.value
                      ? 'gradient-border-1 border-gradient-b-primary-dark'
                      : 'hover:border-system-white36',
                  )}
                  onClick={() => handleAspectChange(item.value)}
                >
                  <CropOptionDisplay option={item} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-between gap-4">
          <Button
            variant="outline"
            className="h-10 border border-system-white24 bg-[#181C23] px-4 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
            onClick={handleReset}
          >
            <BaseLineReset className="mr-2 text-lg" />
            {t('imgCropModal.deleteLast.reset')}
          </Button>
          <div className="flex gap-4">
            <Button variant="outline" className="h-10 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs" onClick={onCancel}>
              {t('create.createForm.upload.cancel')}
            </Button>
            <Button
              variant="primary"
              className="h-10 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
              onClick={handleSaveCropImage}
              disabled={saving}
            >
              {saving && <Loading className="mr-2 animate-spin text-base" />}
              {t('create.createForm.upload.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
